import {
    useLayoutEffect,
    useEffect,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    Animated,
    Platform,
    View
} from "react-native";
import type IMessageScrollerItemProps from "./type";
import type {
    MessageScrollerItemKeyboardEventType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    getSlideStyle,
    usePresence
} from "../../../../helpers/motion";
import {
    MessageScrollerItemContext,
    useMessageScrollerContext
} from "../../context";
import {
    warnOnce
} from "../../../../utils/development";
import MessageScrollerTarget from "../target";

const MessageScrollerItem: FC<IMessageScrollerItemProps> = ({
    isScrollAnchor = false,
    onLayout: onLayoutProp,
    isUnread = false,
    customTheme,
    messageId,
    children,
    style,
    ...props
}) => {
    const context = useMessageScrollerContext();

    const engine = context?.engine;

    const nodeRef = useRef<View | null>(null);

    const isEnteringRef = useRef<boolean | null>(null);

    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    if(isEnteringRef.current === null) {
        isEnteringRef.current = !!engine?.getIsInitialized() && !context?.store.getSnapshot().isLoadingOlder;
    }

    const {
        isReduceMotionEnabled,
        progress
    } = usePresence({
        isInitialAnimated: isEnteringRef.current,
        enterIntent: "surfaceEnter",
        isVisible: true,
        customTheme
    });

    const enterStyle = useMemo(() => getSlideStyle({
        offset: spaces.spacingSm,
        isReduceMotionEnabled,
        progress
    }), [
        isReduceMotionEnabled,
        spaces.spacingSm,
        progress
    ]);

    useEffect(() => {
        if(context) {
            return;
        }

        warnOnce({
            message: "MessageScroller.Item: it must be rendered inside a MessageScroller.Provider."
        });
    }, [
        context
    ]);

    useLayoutEffect(() => {
        return engine?.registerItem(messageId, nodeRef.current, {
            isScrollAnchor,
            isUnread
        });
    }, [
        messageId,
        engine
    ]);

    useLayoutEffect(() => {
        engine?.registerItem(messageId, nodeRef.current, {
            isScrollAnchor,
            isUnread
        });
    }, [
        isScrollAnchor,
        messageId,
        isUnread,
        engine
    ]);

    const onLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            y
        } = event.nativeEvent.layout;

        context?.engine.handleItemLayout(messageId, y, height);

        onLayoutProp?.(event);
    };

    const onKeyDown = (event: MessageScrollerItemKeyboardEventType) => {
        const target = event.target;

        const isArticle = typeof target === "object" && target !== null && "tagName" in target && target.tagName === "ARTICLE";

        if(!isArticle) {
            return;
        }

        if(context?.engine.handleNavigate({
            id: messageId,
            key: event.key
        })) {
            event.preventDefault();
        }
    };

    const webProps = Platform.OS === "web" ? {
        dataSet: {
            messageId
        },
        onKeyDown
    } : {};

    return <MessageScrollerItemContext.Provider
        value={messageId}
    >
        <View
            {...props}
            {...webProps}
            style={[
                style,
                stylesheet.container
            ]}
            onLayout={onLayout}
            ref={nodeRef}
        >
            <MessageScrollerTarget
                customTheme={customTheme}
                messageId={messageId}
            />
            <Animated.View
                style={enterStyle}
            >
                {children}
            </Animated.View>
        </View>
    </MessageScrollerItemContext.Provider>;
};
export default MessageScrollerItem;
